import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Check, X, RefreshCw, UserCheck, UserX, UserPlus } from 'lucide-react';

export default function AdminStaff({ roleFilter }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { loadStaff(); }, []);

  const loadStaff = async () => {
    try {
      const { data } = await api.get('/admin/staff/onboarding');
      setStaff(data.staff || []);
    } catch (err) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (userId, action) => {
    setActionLoading(`${userId}-${action}`);
    try {
      await api.post(`/admin/staff/${userId}/${action}`);
      toast.success(`Staff ${action}ed successfully`);
      loadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = roleFilter ? staff.filter(u => u.role === roleFilter) : staff;

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-[#00a884]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {roleFilter ? `${roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)} Approval` : 'Staff Onboarding'}
          </h2>
          <p className="text-[#8696a0] text-sm mt-1">
            {roleFilter ? `Manage ${roleFilter} approvals` : 'Invite, approve, reject, suspend staff'}
          </p>
        </div>
        <button onClick={loadStaff} className="p-2 text-[#8696a0] hover:text-white transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-[#111b21] rounded-xl border border-[#2a3942] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[#8696a0] text-xs uppercase border-b border-[#2a3942]">
              <th className="px-5 py-3 text-left">Name</th>
              <th className="px-5 py-3 text-left">Email</th>
              <th className="px-5 py-3 text-left">Role</th>
              <th className="px-5 py-3 text-left">Status</th>
              <th className="px-5 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-[#8696a0]">No staff found</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} className="border-b border-[#2a3942] hover:bg-[#1a242a]">
                <td className="px-5 py-3 text-white text-sm">{u.full_name}</td>
                <td className="px-5 py-3 text-[#8696a0] text-sm">{u.email}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    u.role === 'lawyer' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                  }`}>{u.role}</span>
                </td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    u.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    u.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{u.status}</span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    {u.status !== 'active' && (
                      <button onClick={() => doAction(u.id, 'approve')} disabled={actionLoading === `${u.id}-approve`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50">
                        <UserCheck className="w-3 h-3" /> Approve
                      </button>
                    )}
                    {u.status === 'active' && (
                      <button onClick={() => doAction(u.id, 'suspend')} disabled={actionLoading === `${u.id}-suspend`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-lg hover:bg-yellow-700 disabled:opacity-50">
                        <UserX className="w-3 h-3" /> Suspend
                      </button>
                    )}
                    {u.status !== 'active' && u.status !== 'inactive' && (
                      <button onClick={() => doAction(u.id, 'reject')} disabled={actionLoading === `${u.id}-reject`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50">
                        <X className="w-3 h-3" /> Reject
                      </button>
                    )}
                    {u.status === 'suspended' && (
                      <button onClick={() => doAction(u.id, 'reactivate')} disabled={actionLoading === `${u.id}-reactivate`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        <UserPlus className="w-3 h-3" /> Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
